/**
 * The task-type glyphs, as the third copy of one allowlist (`KLQ-20260825-005` §2).
 *
 * TWIN: `TASK_TYPE_ICONS` in `backend/src/kalqa/domains/tasks/models.py` and
 * `ck_workspace_task_type_icon` in migration #29. Three copies, each pointing at the others,
 * because ADR-0010's forced-conversation pattern is what keeps a deliberately duplicated list
 * from drifting. Adding a glyph is a change in all three places: the backend `CHECK` decides
 * what may be stored, and this map decides what can be drawn — a name in one and not the
 * other is a broken glyph on a customer's screen, which is the failure the allowlist exists
 * to prevent.
 *
 * **Names are `lucide-react`'s own kebab-case spelling**, not file paths and not SVG. The map
 * is exhaustive over the union, so TypeScript refuses the build if the union grows an icon this
 * file has no component for — and the union comes straight off the generated client,
 * which is the whole reason the backend keeps `icon` a `Literal` rather than a `str`: a glyph
 * added to the `CHECK` and not to this map fails the build here.
 */

import {
  CalendarClock,
  Camera,
  CircleCheck,
  CircleHelp,
  ClipboardList,
  FileText,
  HardHat,
  MessageSquare,
  MessageSquareWarning,
  Pencil,
  PencilRuler,
  Ruler,
  Search,
  TriangleAlert,
  Users,
  Wrench,
  type LucideIcon,
} from 'lucide-react';

import type { TaskTypeIcon } from './api';

export type { TaskTypeIcon };

/**
 * Every name the backend may store, mapped to the component that draws it.
 *
 * Typed as a total record over the wire union rather than as a lookup that can miss: a
 * `Partial` here would push an "icon not found" branch onto every caller, and the branch that
 * renders nothing is exactly what a customer reports as a bug six months later.
 */
export const TASK_TYPE_ICONS: Readonly<Record<TaskTypeIcon, LucideIcon>> = {
  'pencil-ruler': PencilRuler,
  pencil: Pencil,
  ruler: Ruler,
  'circle-check': CircleCheck,
  search: Search,
  users: Users,
  'message-square': MessageSquare,
  'message-square-warning': MessageSquareWarning,
  'triangle-alert': TriangleAlert,
  'circle-help': CircleHelp,
  'hard-hat': HardHat,
  'clipboard-list': ClipboardList,
  'file-text': FileText,
  camera: Camera,
  'calendar-clock': CalendarClock,
  wrench: Wrench,
};

/** The allowlist in picker order — the same order the backend tuple states it in. */
export const TASK_TYPE_ICON_NAMES = Object.keys(TASK_TYPE_ICONS) as readonly TaskTypeIcon[];
